import { useRef, useState, useCallback, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Play, Pause, ChevronLeft, ChevronRight } from "lucide-react";
import { useSectionTracking, useAnalytics } from "./AnalyticsProvider";
import { useLanguage } from "./LanguageProvider";

const curatedPhotos = [
  {
    src: "/media/photos/new_1.jpg",
    title: "The Specimen",
    desc: "The Bridgette Emerald in its official presentation — 1,055 carats of raw Colombian crystal, exactly as it emerged from the Muzo mines.",
  },
  {
    src: "/media/photos/museum_closed.jpg",
    title: "Museum-Grade Display",
    desc: "Presented in a bespoke glass vitrine. Fewer than five emerald crystals of this scale and quality are known to exist — most reside permanently in museum collections.",
  },
  {
    src: "/media/photos/new_5.jpg",
    title: "Natural Crystal Form",
    desc: "The hexagonal prism structure visible in studio lighting. This natural geometry is the signature of beryl — the mineral family that produces emeralds.",
  },
  {
    src: "/media/photos/museum_open.jpg",
    title: "Private Viewing",
    desc: "The specimen revealed for close inspection. At 1,055 carats with confirmed Muzo origin and zero treatment, it belongs to the rarest category of emerald discoveries ever documented.",
  },
  {
    src: "/media/photos/new_4.jpg",
    title: "On the Presentation Base",
    desc: "1,055.05 carats. 211 grams. 82 x 43 x 36 millimetres. Transparent to translucent with a saturated green and slightly bluish secondary hue.",
  },
  {
    src: "/media/photos/Bridgette.2.jpeg",
    title: "Close Examination",
    desc: "Under controlled light, the saturated green with its characteristic bluish secondary hue becomes apparent — the hallmark of the finest Muzo emeralds.",
  },
  {
    src: "/media/photos/Bridgette.6.jpeg",
    title: "The Crystal Face",
    desc: "No clarity enhancement detected. In a specimen of this magnitude, an untreated state is exceptionally rare — confirmed by Gübelin Report No. 25070096.",
  },
  {
    src: "/media/photos/Bridgette.Gubelin.1.jpeg",
    title: "The Gübelin Profile",
    desc: "The first premium Gemological Profile ever produced for a raw emerald in Gübelin's 170-year history. A distinction no other specimen has received.",
  },
];

const videos = [
  { src: "/media/videos/BridgetteV.1.mp4" },
  { src: "/media/videos/BridgetteV.2.mp4" },
  { src: "/media/videos/BridgetteV.3.mp4" },
  { src: "/media/videos/BridgetteV.4.mp4" },
  { src: "/media/videos/BridgetteV.5.mp4" },
  { src: "/media/videos/BridgetteV.6.mp4" },
  { src: "/media/videos/BridgetteV.7.mp4" },
];

function VideoCarousel({ videos, analytics, containerRef }) {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const vidRef = useRef(null);

  const goTo = useCallback((i) => {
    setActive(i);
    setPlaying(false);
  }, []);

  const toggle = useCallback(() => {
    const vid = vidRef.current;
    if (!vid) return;
    if (playing) {
      vid.pause();
      setPlaying(false);
    } else {
      vid.play().catch(() => {});
      setPlaying(true);
    }
    analytics?.trackClick("video_play", "gallery");
  }, [playing, analytics]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
      {/* Left: Info + Navigation */}
      <div className="lg:col-span-4 flex flex-col justify-center order-2 lg:order-1">
        <p className="text-xs text-[#C5A059]/50 uppercase tracking-[0.2em] mb-4">
          {String(active + 1).padStart(2, "0")} / {String(videos.length).padStart(2, "0")}
        </p>
        <h3 className="font-serif text-2xl lg:text-3xl text-[#F4F0EA] mb-4 leading-snug">
          Specimen in Motion
        </h3>
        <p className="text-base text-[#B8B3AA]/70 leading-relaxed mb-8">
          {playing
            ? "Playing — click the video to pause."
            : "Click the video to play. Each clip captures a different angle and lighting condition of the crystal."
          }
        </p>

        {/* Navigation */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => goTo((active - 1 + videos.length) % videos.length)}
            className="w-11 h-11 flex items-center justify-center border border-[#C5A059]/20 text-[#B8B3AA]/70 hover:text-[#C5A059] hover:border-[#C5A059]/40 transition-all"
            data-testid="video-prev"
          >
            <ChevronLeft size={18} strokeWidth={1.5} />
          </button>
          <div className="flex gap-2">
            {videos.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`h-[2px] transition-all duration-500 ${
                  i === active ? "bg-[#C5A059] w-8" : "bg-[#B8B3AA]/15 w-3 hover:bg-[#B8B3AA]/30"
                }`}
                data-testid={`gallery-video-${i}`}
              />
            ))}
          </div>
          <button
            onClick={() => goTo((active + 1) % videos.length)}
            className="w-11 h-11 flex items-center justify-center border border-[#C5A059]/20 text-[#B8B3AA]/70 hover:text-[#C5A059] hover:border-[#C5A059]/40 transition-all"
            data-testid="video-next"
          >
            <ChevronRight size={18} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Right: Video Player */}
      <div className="lg:col-span-8 order-1 lg:order-2">
        <div
          className="relative bg-[#0A0A09] border border-[#C5A059]/10 overflow-hidden aspect-video cursor-pointer"
          style={{ boxShadow: "20px 20px 60px rgba(0,0,0,0.5)" }}
          onClick={toggle}
          onContextMenu={(e) => e.preventDefault()}
          data-testid="featured-video"
        >
          <video
            ref={vidRef}
            key={videos[active].src}
            src={videos[active].src}
            playsInline
            loop
            preload="auto"
            className="w-full h-full object-cover pointer-events-none"
            onEnded={() => setPlaying(false)}
            onError={() => setPlaying(false)}
            style={{ WebkitUserDrag: "none" }}
          />
          {/* Play/Pause overlay */}
          <AnimatePresence>
            {!playing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 flex items-center justify-center bg-black/25"
              >
                <div className="w-16 h-16 flex items-center justify-center border border-[#C5A059]/40 bg-[#111110]/30 backdrop-blur-sm">
                  <Play size={24} strokeWidth={1.5} className="text-[#C5A059] ml-1" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="absolute inset-0 z-10" onContextMenu={(e) => e.preventDefault()} style={{ background: "transparent", pointerEvents: "none" }} />
        </div>
      </div>
    </div>
  );
}

function ProtectionOverlay() {
  return (
    <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden opacity-[0.05]">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="absolute text-[#C5A059] font-serif text-sm whitespace-nowrap"
          style={{
            transform: "rotate(-30deg)",
            top: `${i * 18}%`,
            left: "-10%",
            width: "140%",
            textAlign: "center",
            letterSpacing: "0.3em",
          }}
        >
          CONFIDENTIAL — THE BRIDGETTE EMERALD — CONFIDENTIAL — THE BRIDGETTE EMERALD
        </div>
      ))}
    </div>
  );
}

export default function GallerySection() {
  const sectionRef = useSectionTracking("gallery");
  const { t } = useLanguage();
  const g = t.gallery;
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-80px" });
  const analytics = useAnalytics();
  const [current, setCurrent] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  // Block right-click on entire gallery
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const prevent = (e) => { e.preventDefault(); return false; };
    el.addEventListener("contextmenu", prevent);
    el.addEventListener("dragstart", prevent);
    return () => {
      el.removeEventListener("contextmenu", prevent);
      el.removeEventListener("dragstart", prevent);
    };
  }, []);

  const goTo = useCallback((i) => {
    setCurrent(i);
    setAutoPlay(false);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setAutoPlay(true), 12000);
  }, []);

  const next = useCallback(() => goTo((current + 1) % curatedPhotos.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + curatedPhotos.length) % curatedPhotos.length), [current, goTo]);

  useEffect(() => {
    if (!autoPlay) return;
    const iv = setInterval(() => setCurrent(c => (c + 1) % curatedPhotos.length), 6000);
    return () => clearInterval(iv);
  }, [autoPlay]);

  const photo = curatedPhotos[current];

  return (
    <section
      id="gallery"
      ref={sectionRef}
      className="relative py-12 lg:py-18 select-none"
      style={{ WebkitUserSelect: "none", MozUserSelect: "none", userSelect: "none" }}
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12" ref={(el) => { animRef.current = el; containerRef.current = el; }}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="overline mb-4">{g.overline}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter">
            {g.title} <span className="italic text-[#C5A059]">{g.title_accent}</span>
          </h2>
        </motion.div>

        {/* CURATED CAROUSEL — description left, image right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center" data-testid="cinematic-slider">
          {/* Left: Description */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-4 flex flex-col justify-center order-2 lg:order-1"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
              >
                <p className="text-xs text-[#C5A059]/50 uppercase tracking-[0.2em] mb-4">
                  {String(current + 1).padStart(2, "0")} / {String(curatedPhotos.length).padStart(2, "0")}
                </p>
                <h3 className="font-serif text-2xl lg:text-3xl text-[#F4F0EA] mb-4 leading-snug">
                  {photo.title}
                </h3>
                <p className="text-base text-[#B8B3AA]/70 leading-relaxed mb-8">
                  {photo.desc}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center gap-4">
              <button
                onClick={prev}
                className="w-11 h-11 flex items-center justify-center border border-[#C5A059]/20 text-[#B8B3AA]/70 hover:text-[#C5A059] hover:border-[#C5A059]/40 transition-all"
                data-testid="slider-prev"
              >
                <ChevronLeft size={18} strokeWidth={1.5} />
              </button>
              <div className="flex gap-2">
                {curatedPhotos.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    className={`h-[2px] transition-all duration-500 ${
                      i === current ? "bg-[#C5A059] w-8" : "bg-[#B8B3AA]/15 w-4 hover:bg-[#B8B3AA]/30"
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={next}
                className="w-11 h-11 flex items-center justify-center border border-[#C5A059]/20 text-[#B8B3AA]/70 hover:text-[#C5A059] hover:border-[#C5A059]/40 transition-all"
                data-testid="slider-next"
              >
                <ChevronRight size={18} strokeWidth={1.5} />
              </button>
            </div>
          </motion.div>

          {/* Right: Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-8 order-1 lg:order-2"
          >
            <div
              className="relative bg-[#1A1918] border border-[#C5A059]/10 p-3 sm:p-4"
              style={{ boxShadow: "20px 20px 60px rgba(0,0,0,0.5)" }}
            >
              <div className="relative overflow-hidden aspect-[4/3]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={photo.src}
                    src={photo.src}
                    alt={photo.title}
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    className="w-full h-full object-contain bg-[#111110] pointer-events-none"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                    style={{ WebkitUserDrag: "none", userDrag: "none", WebkitTouchCallout: "none" }}
                  />
                </AnimatePresence>
                {/* Transparent overlay to block direct interaction */}
                <div className="absolute inset-0 z-10" onContextMenu={(e) => e.preventDefault()} style={{ background: "transparent" }} />
                <ProtectionOverlay />
              </div>
            </div>
          </motion.div>
        </div>

        {/* VIDEO SECTION */}
        <div className="mt-24 lg:mt-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-12"
          >
            <p className="overline mb-4">In Motion</p>
            <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F4F0EA] font-light tracking-tighter">
              Video <span className="italic text-[#C5A059]">Showcase</span>
            </h3>
          </motion.div>

          <VideoCarousel videos={videos} analytics={analytics} />
        </div>
      </div>
    </section>
  );
}
